import { useTranslation } from 'react-i18next';
import type { AuthUser, Organization } from '@nargus/shared';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  BellRing,
  Building2,
  Check,
  ChevronsUpDown,
  Images,
  LayoutDashboard,
  LogOut,
  Plus,
  UserCircle,
  Users,
  Wallet,
} from 'lucide-react';
import { useLogout } from '@/resources/auth/auth.service';
import { UserAvatar } from '@/components/user-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { EmptyNote } from './ui';

const ROW = 'h-11 gap-3 text-base md:h-8 md:gap-2 md:text-sm';

export function AppSidebar({
  user,
  organizations,
  activeOrganizationId,
}: {
  user: AuthUser;
  organizations: Organization[];
  activeOrganizationId: string | null;
}) {
  const { t } = useTranslation();
  const { setOpenMobile, isMobile } = useSidebar();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const logout = useLogout();
  const label = user.name ?? user.email;

  const activeOrganization =
    organizations.find((org) => org.id === activeOrganizationId) ?? null;

  function close() {
    if (isMobile) setOpenMobile(false);
  }

  function signOut() {
    logout();
    void navigate({ to: '/login', search: { next: undefined } });
  }

  const isSection = (section: 'members' | 'reminders' | 'debts' | 'photos') =>
    Boolean(
      activeOrganization &&
        matchRoute({
          to: `/organizations/$organizationId/${section}`,
          params: { organizationId: activeOrganization.id },
          fuzzy: true,
        }),
    );

  const isOverview = Boolean(
    activeOrganization &&
      matchRoute({
        to: '/organizations/$organizationId',
        params: { organizationId: activeOrganization.id },
      }),
  );

  /** Freunde sehen die Arbeitsdaten nicht, also auch nicht ihre Einträge. */
  const showWork = activeOrganization?.role !== 'friend';

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <img
          src="/assets/logo-nargus.png"
          alt="nargus"
          className="h-7 w-auto self-start mix-blend-multiply dark:mix-blend-screen dark:invert"
        />
      </SidebarHeader>

      <SidebarSeparator />

      {}
      <SidebarContent>
        {activeOrganization ? (
          <SidebarGroup>
            <SidebarGroupLabel>{activeOrganization.name}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isOverview}
                    className={ROW}
                    onClick={close}
                    render={
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: activeOrganization.id }}
                      />
                    }
                  >
                    <LayoutDashboard />
                    <span>{t('nav.overview')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isSection('members')}
                    className={ROW}
                    onClick={close}
                    render={
                      <Link
                        to="/organizations/$organizationId/members"
                        params={{ organizationId: activeOrganization.id }}
                      />
                    }
                  >
                    <Users />
                    <span>{t('nav.members')}</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>{activeOrganization.memberCount}</SidebarMenuBadge>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isSection('photos')}
                    className={ROW}
                    onClick={close}
                    render={
                      <Link
                        to="/organizations/$organizationId/photos"
                        params={{ organizationId: activeOrganization.id }}
                      />
                    }
                  >
                    <Images />
                    <span>{t('photos.title')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {showWork && (
                  <>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isSection('reminders')}
                        className={ROW}
                        onClick={close}
                        render={
                          <Link
                            to="/organizations/$organizationId/reminders"
                            params={{ organizationId: activeOrganization.id }}
                          />
                        }
                      >
                        <BellRing />
                        <span>{t('reminders.title')}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isSection('debts')}
                        className={ROW}
                        onClick={close}
                        render={
                          <Link
                            to="/organizations/$organizationId/debts"
                            params={{ organizationId: activeOrganization.id }}
                          />
                        }
                      >
                        <Wallet />
                        <span>{t('debts.title')}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <EmptyNote>{t('nav.noOrganization')}</EmptyNote>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton
                    size="lg"
                    aria-label={t('nav.userMenu')}
                    className="h-14 gap-3 md:h-12"
                  />
                }
              >
                <UserAvatar
                  userId={user.id}
                  name={user.name}
                  email={user.email}
                  avatarUpdatedAt={user.avatarUpdatedAt}
                />
                <div className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm font-medium">{label}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <ChevronsUpDown className="text-muted-foreground" />
              </DropdownMenuTrigger>

              <DropdownMenuContent
                side={isMobile ? 'top' : 'right'}
                align="end"
                className="min-w-56"
              >
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{t('nav.organizations')}</DropdownMenuLabel>
                  {organizations.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => {
                        void navigate({
                          to: '/organizations/$organizationId',
                          params: { organizationId: org.id },
                        });
                        close();
                      }}
                    >
                      <Building2 />
                      <span className="min-w-0 flex-1 truncate">{org.name}</span>
                      {org.id === activeOrganization?.id && <Check className="size-4" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem
                    onClick={() => {
                      void navigate({ to: '/organizations/new' });
                      close();
                    }}
                  >
                    <Plus />
                    {t('nav.newOrganization')}
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => {
                    void navigate({ to: '/account' });
                    close();
                  }}
                >
                  <UserCircle />
                  {t('nav.account')}
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={signOut}>
                  <LogOut />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
